import ShopArrowRightIcon from '@/components/icons/shop/ArrowRightIcon';
import { useTickSoundInteraction } from '@/hooks';
import { useConfigStore } from '@/stores';
import type { EventListItem } from '@/types/event';
import { getEventCardAriaLabel, getRawPeriodText, isEventClickable } from '@/utils/event';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './EventListCard.module.scss';

interface EventListCardProps {
    item: EventListItem;
    tabIndex?: number;
    isHalfScreen?: boolean;
    onClick?: (item: EventListItem) => void;
}

export default function EventListCard({ item, tabIndex = 3, isHalfScreen = false, onClick }: EventListCardProps) {
    const { handleInteraction, handleKeyboardInteraction } = useTickSoundInteraction();
    const { t } = useTranslation();
    const language = useConfigStore((state) => state.config.ui.language);
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);
    const clickable = isEventClickable(item, buildingMenus, language);
    const periodText = getRawPeriodText(item.startDtime, item.endDtime);
    const hasSubtitle = Boolean(item.subtitle?.trim() && item.subtitle.trim() !== item.title?.trim());
    const viewMoreLabel = t('event.button.view_more');

    const handleSelect = () => {
        if (clickable) onClick?.(item);
    };

    return (
        <div
            key={item.id}
            role={clickable ? 'button' : undefined}
            data-type={clickable ? 'button' : undefined}
            data-role={clickable ? t('common.button.tts.popup_button') : undefined}
            tabIndex={tabIndex}
            aria-label={getEventCardAriaLabel(item)}
            aria-disabled={!clickable || undefined}
            data-interactive
            className={clsx(styles.card, 'focus-border', {
                [styles['card--clickable']]: clickable,
                [styles['card--disabled']]: !clickable,
                [styles['card--half']]: isHalfScreen,
            })}
            onClick={(e) => clickable && handleInteraction(e, handleSelect)}
            onKeyDown={(e) => clickable && handleKeyboardInteraction(e, handleSelect)}
        >
            <div className={styles.card__image}>
                <img src={item.imageUrl} alt="" className={styles.card__img} loading="lazy" />
            </div>
            <div className={styles.card__body}>
                <div className={styles.card__titles}>
                    <p className={styles.card__title}>{item.title}</p>
                    {hasSubtitle && <p className={styles.card__desc}>{item.subtitle}</p>}
                </div>
                <div className={styles.card__meta}>
                    <div className={styles.card__actionSlot}>
                        {clickable && (
                            <span className={styles.card__more} aria-hidden>
                                {viewMoreLabel}
                                <ShopArrowRightIcon className={styles.card__moreIcon} width="100%" height="100%" color="currentColor" />
                            </span>
                        )}
                        {!clickable && periodText && <p className={styles.card__period}>{periodText}</p>}
                    </div>
                    {periodText && (
                        <div className={styles.card__periodSlot} aria-hidden={!clickable}>
                            {clickable && <p className={styles.card__period}>{periodText}</p>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
